import { ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { LocalizedText, pickLocalizedText } from '../../../common/models/billing.model';

/** Languages the invoice free text can be written in (the app's languages). */
export const CONTENT_LANGS = ['en', 'fr', 'es', 'ar'];

/** A per-language map for editing (a legacy plain string becomes English). */
export const asLangMap = (v?: string | LocalizedText): Record<string, string> =>
  typeof v === 'string' ? (v ? { en: v } : {}) : { ...(v || {}) };

/**
 * One labelled settings input. Defined at module level on purpose: a component
 * declared inside a page's render is a new type on every render, so React
 * remounts its children on each keystroke and the input loses focus.
 */
export function SettingsField({
  id,
  label,
  help,
  className,
  children,
}: {
  id: string;
  label: ReactNode;
  help?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('grid content-start gap-2', className)}>
      <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">{label}</Label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

/** A multiline text written per language: language pills and the text of the chosen one. */
export function LocalizedTextField({
  id,
  label,
  value,
  lang,
  onLangChange,
  onChange,
  langLabel,
}: {
  id: string;
  label: ReactNode;
  value?: string | LocalizedText;
  lang: string;
  onLangChange: (lang: string) => void;
  onChange: (lang: string, value: string) => void;
  langLabel?: string;
}) {
  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-medium text-muted-foreground">{label}</Label>
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={lang}
          onValueChange={(v) => v && onLangChange(v)}
          aria-label={langLabel}
        >
          {CONTENT_LANGS.map((code) => (
            <ToggleGroupItem key={code} value={code} className="px-2 text-xs">
              {code.toUpperCase()}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>
      <Textarea
        id={id}
        rows={2}
        value={pickLocalizedText(value, lang)}
        onChange={(e) => onChange(lang, e.target.value)}
        dir={lang === 'ar' ? 'rtl' : 'ltr'}
      />
    </div>
  );
}
